<template>
  <div class="container px-0 mb-2" id="invoice-management">
    <CardRoot class="border-translucent">
      <CardHeader>
        <CardTitle>{{
          $t("dashboard.companyData.invoice_management")
        }}</CardTitle>
      </CardHeader>
      <CardBody class="pt-0">
        <TabsContainer
          :tabs="tabs"
          @change:tab="setTab"
          :activeTab="activeTab || 'invoice-history'"
        >
          <template #tab-submit-invoice>
            <RequestInvoiceComponent
              v-if="!isMultiCompany && company"
              :company="company"
              :pendingInvoiceItems="pendingInvoiceItems"
              :defaultInvoiceNumber="defaultInvoiceNumber"
              @invoice-requested="handleInvoiceRequested"
            />
          </template>
          <template #tab-invoice-history>
            <!-- Single-company dashboard passes an explicit `company`; the
                 consolidated view passes a `companies` list whose length changes
                 as companies are (de)selected. Render for any non-empty input. -->
            <InvoiceHistoryComponent
              v-if="company?.id || companiesList.length > 0"
              :company_id="company?.id"
              :companies="company ? [company] : companiesList"
              :is_admin="isAdmin ?? false"
            />
          </template>
          <template #tab-sales-transactions>
            <SalesTransactionsComponent
              :company-ids="companyIds"
              :companies="companiesList"
              :nda_signed_link="ndaSignedLink"
            />
          </template>
          <template #tab-cancellation-documents>
            <CancellationStatementsComponent
              :companies="companiesList"
              :is_admin="isAdmin ?? false"
            />
          </template>
          <template #tab-documents>
            <UnifiedDocumentsTable
              :companies="companiesList"
              :isAdmin="isAdmin ?? false"
              :showCompanyColumn="isMultiCompany ?? false"
            />
          </template>
        </TabsContainer>
      </CardBody>
    </CardRoot>
  </div>
</template>

<script setup lang="ts">
import {
  CardBody,
  CardHeader,
  CardRoot,
  CardTitle,
} from "@/components/common/card";
import TabsContainer from "@/components/common/tabs/TabsContainer.vue";
import { hasPositivePendingPayout } from "@/composables/money/moneyUtils";
import { useTabs } from "@/composables/useTabs";
import CancellationStatementsComponent from "@/dashboard/invoice_management/CancellationStatementsComponent.vue";
import UnifiedDocumentsTable from "@/dashboard/invoice_management/documents/UnifiedDocumentsTable.vue";
import InvoiceHistoryComponent from "@/money/invoice/InvoiceHistoryComponent.vue";
import RequestInvoiceComponent from "@/money/invoice/RequestInvoiceComponent.vue";
import SalesTransactionsComponent from "@/money/SalesTransactionsComponent.vue";
import type { Company, InvoiceItem } from "@/types";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  company?: Company;
  companies?: Company[];
  isAdmin?: boolean;
  pendingInvoiceItems: InvoiceItem[];
  defaultInvoiceNumber?: string;
  initialTab?: string;
}>();

const emit = defineEmits<{
  "invoice-requested": [];
}>();

const { t } = useI18n();

const isMultiCompany = computed(() => {
  return props.companies && props.companies.length > 1;
});

const canSubmitInvoice = computed(() =>
  hasPositivePendingPayout(props.pendingInvoiceItems)
);

const companiesList = computed(() => {
  if (props.companies && props.companies.length > 0) {
    return props.companies;
  } else if (props.company) {
    return [props.company];
  }
  return [];
});

const companyIds = computed(() => {
  return companiesList.value
    .map((company) => company.id)
    .filter((id) => id !== undefined) as number[];
});

const ndaSignedLink = computed(() => {
  if (companiesList.value.length > 0) {
    return companiesList.value[0].nda_signed_link || "";
  }
  return "";
});

const tabs = computed(() => {
  const availableTabs = [];

  if (!isMultiCompany.value) {
    availableTabs.push({
      id: "submit-invoice",
      name: t("dashboard.companyData.submit_invoice"),
      icon: "file-invoice-dollar",
      disabled: false,
    });
  }

  availableTabs.push(
    {
      id: "invoice-history",
      name: t("dashboard.companyData.invoice_history"),
      icon: "history",
      disabled: false,
    },
    {
      id: "sales-transactions",
      name: t("dashboard.sales_transactions.title"),
      icon: "exchange-alt",
      disabled: false,
    },
    {
      id: "documents",
      name: t("common.documents"),
      icon: "file-alt",
      disabled: false,
    },
    {
      id: "cancellation-documents",
      name: t("dashboard.companyData.cancellation_documents"),
      icon: "file-check",
      disabled: false,
    }
  );

  return availableTabs;
});

const { activeTab, setTab } = useTabs("invoice-management", {
  defaultTabId:
    props.initialTab ||
    (!isMultiCompany.value && canSubmitInvoice.value
      ? "submit-invoice"
      : "invoice-history"),
  tabs: tabs,
});

const handleInvoiceRequested = () => {
  emit("invoice-requested");
};
</script>
